<template>
  <v-menu v-model="open" offset-y left nudge-bottom="6" min-width="224">
    <template v-slot:activator="{ on, attrs }">
      <button type="button" class="user-menu__trigger" :class="{ 'user-menu__trigger--open': open }" v-bind="attrs" v-on="on" :title="nom" aria-label="Menu utilisateur" aria-haspopup="menu" :aria-expanded="String(open)">
        <span class="user-menu__avatar">{{ initials }}</span>
        <span class="user-menu__name d-none d-sm-inline">{{ nom }}</span>
        <v-icon size="18" class="user-menu__chevron d-none d-sm-inline-flex" :class="{ 'user-menu__chevron--open': open }">expand_more</v-icon>
      </button>
    </template>
    <v-list dense>
      <v-list-item two-line>
        <v-list-item-content>
          <v-list-item-title>{{ nom }}</v-list-item-title>
          <v-list-item-subtitle>{{ email }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider />
      <v-list-item @click="copyUserSerial">
        <v-list-item-icon><v-icon small>content_copy</v-icon></v-list-item-icon>
        <v-list-item-title>Copier mon numéro de série</v-list-item-title>
      </v-list-item>
      <v-list-item v-if="hasCurrentAccount" :disabled="accountLoading" @click="confirmRemoveAccount">
        <v-list-item-icon><v-icon small>person_remove</v-icon></v-list-item-icon>
        <v-list-item-title>Supprimer mon compte {{ contextLabel }}</v-list-item-title>
      </v-list-item>
      <v-divider />
      <v-list-item @click="logout">
        <v-list-item-icon><v-icon small>logout</v-icon></v-list-item-icon>
        <v-list-item-title>Se déconnecter</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
  import Vue from 'vue'
  import { mapGetters, mapState } from 'vuex'
  import { getUserProfile } from '../utils/Keycloak'

  // Utilisateur connecté, en haut à droite : identité, numéro de série, déconnexion
  export default {
    name: 'UserMenu',
    data () {
      return {
        nom: '',
        email: '',
        userProfile: null,
        accountLoading: false,
        open: false
      }
    },
    computed: {
      ...mapState({
        app: state => state.home.app,
        customers: state => state.customers.list
      }),
      ...mapGetters(['hasCurrentAccount']),
      contextLabel () {
        return this.app === 'carnot' ? 'Boucherie' : 'Poissonnerie'
      },
      initials () {
        return this.nom.split(' ').filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase()
      }
    },
    methods: {
      // Supprime le compte de test de l'utilisateur dans le rayon courant (restaurable ensuite)
      confirmRemoveAccount () {
        if (!confirm(`Supprimer votre compte ${this.contextLabel} ? Vous pourrez le restaurer ensuite.`)) return
        this.accountLoading = true
        this.$store.dispatch('removeOwnAccount')
          .catch(() => {})
          .finally(() => { this.accountLoading = false })
      },
      logout () {
        Vue.prototype.$keycloak.logoutFn()
      },
      copyUserSerial () {
        const clientIdAttr = this.app === 'carnot' ? 'carnot_clientid' : 'hublot_clientid'
        const userClientId = this.userProfile?.attributes?.[clientIdAttr]?.[0]
        if (!userClientId) {
          this.$store.dispatch('addNotification', { text: 'Aucun identifiant client trouvé pour ce contexte', color: 'warning' })
          return
        }
        const customer = this.customers.find(shop => shop.clientId === parseInt(userClientId, 10))
        const serial = customer?.serial
        if (!serial) {
          this.$store.dispatch('addNotification', { text: 'Aucun numéro de série trouvé pour votre compte', color: 'warning' })
          return
        }
        navigator.clipboard.writeText(serial)
          .then(() => this.$store.dispatch('addNotification', { text: `Numéro de série ${serial} copié dans le presse-papier`, color: 'success' }))
          .catch(() => this.$store.dispatch('addNotification', { text: 'Impossible de copier le numéro de série', color: 'error' }))
      }
    },
    created () {
      getUserProfile().then(profile => {
        this.nom = `${profile.firstName} ${profile.lastName}`
        this.email = profile.email || ''
        this.userProfile = profile
      })
    }
  }
</script>

<style lang="scss" scoped>
.user-menu__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--control);
  padding: 0 var(--space-1) 0 3px;
  min-width: var(--control);
  border: 0;
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--ink);
  font: inherit;
  cursor: pointer;

  &:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }
  // Posé sur la barre teintée métier : le survol suit la même teinte que la barre latérale
  &--open { background: var(--nav-hover); }
  @media (hover: hover) and (pointer: fine) {
    &:hover { background: var(--nav-hover); }
  }
}
.user-menu__chevron.v-icon {
  color: var(--ink-muted);
  transition: transform 160ms var(--hb-ease-out);
  &--open { transform: rotate(180deg); }
}
@media (prefers-reduced-motion: reduce) {
  .user-menu__chevron.v-icon { transition: none; }
}
.user-menu__avatar {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background: var(--brand-soft);
  color: var(--brand);
  font-size: 12px;
  font-weight: 600;
}
.user-menu__name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
</style>
